import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { CalendarCheck, CaretLeft, CaretRight } from "@phosphor-icons/react";

const fmtDay = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

/**
 * Client self-booking — pick an available slot for a job (`jobId`) or a preferred slot on a request (`requestId`).
 * Renders a button; the slot picker opens in a dialog. onBooked(result) is called after a successful booking.
 */
export default function BookAppointment({ jobId, requestId, scheduled, onBooked, label }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(0);
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setData(null); setErr("");
    API.get("/client-hub/booking/slots", { params: jobId ? { job_id: jobId } : {} }).then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Booking isn't available right now"));
  }, [jobId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const days = (data?.days || []).filter((d) => d.any_available);
  const perPage = 4;
  const visible = days.slice(page * perPage, page * perPage + perPage);
  const confirm = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const url = jobId ? `/client-hub/jobs/${jobId}/book` : `/client-hub/requests/${requestId}/book`;
      const { data: res } = await API.post(url, { start: pick.start, note });
      toast.success(res.message || "Booked");
      setOpen(false); setPick(null); setNote("");
      onBooked && onBooked(res);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't book that slot");
      if (e?.response?.status === 409) load();
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant={scheduled ? "outline" : "default"} onClick={() => setOpen(true)} data-testid="book-appointment-btn"><CalendarCheck size={14} className="mr-1" /> {label || (scheduled ? "Change appointment" : jobId ? "Book an appointment" : "Choose a preferred time")}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="book-appointment-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">{jobId ? "Book your appointment" : "Preferred appointment time"}</DialogTitle></DialogHeader>
          {err ? <p className="text-sm text-muted-foreground" data-testid="book-error">{err}</p> : !data ? <div className="h-40 animate-pulse rounded-lg bg-muted/40" /> : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{data.settings.message}{!jobId ? " We'll do our best to confirm this time once the request is approved." : ""}</p>
              {days.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="book-no-slots">No slots are open in the next {data.settings.days_ahead} days — message us and we'll find a time.</p> : (
                <>
                  <div className="flex items-center justify-between"><Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="book-prev"><CaretLeft size={14} /></Button><span className="text-xs text-muted-foreground">{fmtDay(visible[0].date)} – {fmtDay(visible[visible.length - 1].date)}</span><Button size="sm" variant="ghost" disabled={(page + 1) * perPage >= days.length} onClick={() => setPage(page + 1)} data-testid="book-next"><CaretRight size={14} /></Button></div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {visible.map((d) => (
                      <div key={d.date} className="space-y-1" data-testid={`book-day-${d.date}`}>
                        <p className="text-xs font-semibold text-center">{fmtDay(d.date)}</p>
                        {d.slots.map((s) => <button key={s.start} disabled={!s.available} onClick={() => setPick(s)} className={`w-full rounded-md border px-2 py-1.5 text-xs ${pick?.start === s.start ? "border-primary bg-primary text-primary-foreground" : s.available ? "border-border hover:border-primary/60" : "border-border text-muted-foreground/40 line-through cursor-not-allowed"}`} data-testid={`book-slot-${s.start}`}>{s.label}</button>)}
                      </div>
                    ))}
                  </div>
                  {pick && <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm" data-testid="book-picked">Selected: <strong>{fmtDay(pick.start.slice(0, 10))}, {pick.label}</strong></div>}
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know? (access, parking, who'll be there)" data-testid="book-note" />
                </>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={confirm} disabled={!pick || busy} data-testid="book-confirm">{busy ? "Booking…" : jobId ? "Confirm appointment" : "Save preferred time"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
